import { Link, Outlet, useLocation } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

export default function Layout({ darkMode, toggleDarkMode }: { darkMode: boolean; toggleDarkMode: () => void }) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        setDropdownOpen(false); // close dropdown when route changes
    }, [location.pathname]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !(dropdownRef.current as any).contains(event.target)
            ) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className={`min-h-screen flex flex-col ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'}`}>
            <header
                className={`sticky top-0 z-50 px-6 py-4 flex justify-between items-center shadow-md ${
                    darkMode
                        ? 'bg-gray-800 backdrop-blur text-white'
                        : 'bg-white/70 backdrop-blur text-gray-900'
                }`}
            >
                <h1 className="text-2xl font-bold tracking-tight">Nebula Network</h1>
                <nav className="relative flex gap-6 items-center text-base font-medium">
                    <Link to="/" className="hover:underline">Home</Link>
                    <div className="relative" ref={dropdownRef}>
                        <button
                            className="hover:underline"
                            onClick={() => setDropdownOpen((open) => !open)}
                        >
                            Code Crackers ▾
                        </button>
                        <AnimatePresence>
                            {dropdownOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.2 }}
                                    className={`absolute top-full mt-2 w-48 p-2 rounded-lg shadow-xl ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} border border-gray-300 z-50`}
                                >
                                    <Link to="/calculators/sv" className="block px-2 py-1 hover:bg-indigo-500 hover:text-white rounded">Shattered Veil</Link>
                                    <Link to="/calculators/terminus" className="block px-2 py-1 hover:bg-indigo-500 hover:text-white rounded">Terminus</Link>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    <Link to="/lobby" className="hover:underline">Bot Lobby</Link>
                    <div className="flex items-center gap-2">
                        <span className="text-sm">Dark Mode</span>
                        <Switch checked={darkMode} onCheckedChange={toggleDarkMode} />
                    </div>
                </nav>
            </header>

            <main className="flex-1">
                <Outlet />
            </main>

            <footer className="p-4 text-center text-sm border-t border-gray-300 bg-transparent">
                © {new Date().getFullYear()} Nebula Network. All rights reserved.
            </footer>
        </div>
    );
}
